import { normalize } from 'normalizr'
import { Schemas } from 'redux-modules/schema'
import { updateEntities, deleteEntity } from 'redux-modules/entity'

import api from 'api'

const FETCH_REQUEST = 'realhub/providerDeadlineGroup/FETCH_REQUEST'
const FETCH_SUCCESS = 'realhub/providerDeadlineGroup/FETCH_SUCCESS'
const FETCH_FAILURE = 'realhub/providerDeadlineGroup/FETCH_FAILURE'
const CREATE_REQUEST = 'realhub/providerDeadlineGroup/CREATE_REQUEST'
const CREATE_SUCCESS = 'realhub/providerDeadlineGroup/CREATE_SUCCESS'
const CREATE_FAILURE = 'realhub/providerDeadlineGroup/CREATE_FAILURE'
const UPDATE_REQUEST = 'realhub/providerDeadlineGroup/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realhub/providerDeadlineGroup/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realhub/providerDeadlineGroup/UPDATE_FAILURE'
const DELETE_REQUEST = 'realhub/providerDeadlineGroup/DELETE_REQUEST'
const DELETE_SUCCESS = 'realhub/providerDeadlineGroup/DELETE_SUCCESS'
const DELETE_FAILURE = 'realhub/providerDeadlineGroup/DELETE_FAILURE'

// Initial State
const initialState = {
  loaded: false,
  loadedForProviderIds: [],
  loading: false,
  creating: false,
  updating: false,
  deleting: false,
  errors: [],
}

// Actions
export function fetchRequest(payload = {}){
  return {
    type: FETCH_REQUEST,
    loadedForProviderIds: payload.loadedForProviderIds,
  }
}

export function fetchSuccess(){
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors = []){
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(){
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(payload = {}){
  return {
    type: CREATE_SUCCESS,
    result: payload.result,
  }
}

export function createFailure(errors = []){
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(){
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(){
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors = []){
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest(){
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess(){
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors = []){
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Helper Functions
const handleError = errors => errors || ['Something went wrong']

// Action Creators
export function loadProviderDeadlineGroups(providerId, options){
  return (dispatch, getState) => {
    // Set Loading and add key to loaded array
    const providerDeadlineGroups = getState().providerDeadlineGroups
    const loadedForProviderIds = providerDeadlineGroups.loadedForProviderIds
      ? providerDeadlineGroups.loadedForProviderIds.slice()
      : []
    if (!loadedForProviderIds.includes(providerId)){
      loadedForProviderIds.push(providerId)
    }
    dispatch(fetchRequest({ loadedForProviderIds }))

    const promise = api(`/admin/providers/${providerId}/deadline_groups.json`, options)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.PROVIDER_DEADLINE_GROUP_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess(normalizedJson))
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(fetchFailure(errors))
      })

    return promise
  }
}

export function createProviderDeadlineGroup(providerDeadlineGroup, options, callback){
  const config = {
    method: 'POST',
    body: JSON.stringify({
      provider_deadline_group: providerDeadlineGroup,
    }),
  }

  return (dispatch) => {
    // Set Loading
    dispatch(createRequest())

    const url = `/admin/providers/${providerDeadlineGroup.provider_id}/deadline_groups.json`
    const promise = api(url, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.PROVIDER_DEADLINE_GROUP)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        if (callback) callback(json)
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(createFailure(errors))
      })

    return promise
  }
}

export function updateProviderDeadlineGroup(providerDeadlineGroup, options, callback){
  const config = {
    method: 'PUT',
    body: JSON.stringify({
      provider_deadline_group: providerDeadlineGroup,
    }),
  }

  return (dispatch) => {
    // Set Loading
    dispatch(updateRequest())

    const { id, provider_id } = providerDeadlineGroup
    const url = `/admin/providers/${provider_id}/deadline_groups/${id}.json`
    const promise = api(url, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.PROVIDER_DEADLINE_GROUP)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        if (callback) callback(json)
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))
      })

    return promise
  }
}

export function deleteProviderDeadlineGroup(providerDeadlineGroup, options, callback){
  const config = {
    method: 'DELETE',
  }

  return (dispatch) => {
    // Set Loading
    dispatch(deleteRequest())

    const { provider_id, id } = providerDeadlineGroup
    const url = `/admin/providers/${provider_id}/deadline_groups/${id}.json`
    const promise = api(url, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.PROVIDER_DEADLINE_GROUP)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        if (callback) callback(json)
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(deleteFailure(errors))
      })

    return promise
  }
}

// Reducer
export default function reducer(state = initialState, action = {}){
  switch (action.type){
    case FETCH_REQUEST:
      return Object.assign({}, state, {
        loading: true,
        loadedForProviderIds: action.loadedForProviderIds || state.loadedForProviderIds,
      })
    case FETCH_SUCCESS:
      return Object.assign({}, state, {
        loading: false,
        loaded: true,
        errors: [],
      })
    case FETCH_FAILURE:
      return Object.assign({}, state, {
        loading: false,
        loaded: false,
        errors: action.errors,
      })
    case CREATE_REQUEST:
      return Object.assign({}, state, {
        creating: true,
      })
    case CREATE_SUCCESS:
      return Object.assign({}, state, {
        creating: false,
        errors: [],
      })
    case CREATE_FAILURE:
      return Object.assign({}, state, {
        creating: false,
        errors: action.errors,
      })
    case UPDATE_REQUEST:
      return Object.assign({}, state, {
        updating: true,
      })
    case UPDATE_SUCCESS:
      return Object.assign({}, state, {
        updating: false,
        errors: [],
      })
    case UPDATE_FAILURE:
      return Object.assign({}, state, {
        updating: false,
        errors: action.errors,
      })
    case DELETE_REQUEST:
      return Object.assign({}, state, {
        deleting: true,
      })
    case DELETE_SUCCESS:
      return Object.assign({}, state, {
        deleting: false,
        errors: [],
      })
    case DELETE_FAILURE:
      return Object.assign({}, state, {
        deleting: false,
        errors: action.errors,
      })
    default:
      return state
  }
}
